import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  DocumentTextIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  CreditCardIcon,
  CheckCircleIcon,
  AcademicCapIcon,
  CalendarDaysIcon,
  UsersIcon,
  PlusIcon,
  MinusIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline';

const ApplyOnline = () => {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    studentName: '',
    gender: '',
    dateOfBirth: '',
    placeOfBirth: '',
    religion: '',
    nationality: '',
    email: '',
    fatherName: '',
    address: '',
    phone: '',
    cell: '',
    class: '',
    admissionDate: '',
    lastSchool: '',
    reasonForLeaving: '',
    siblings: [{ name: '', class: '', rollNumber: '' }],
    applicationDate: '',
    signature: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle sibling changes
  const handleSiblingChange = (index, field, value) => {
    const newSiblings = [...formData.siblings];
    newSiblings[index][field] = value;
    setFormData(prev => ({
      ...prev,
      siblings: newSiblings
    }));
  };

  // Add new sibling
  const addSibling = () => {
    setFormData(prev => ({
      ...prev,
      siblings: [...prev.siblings, { name: '', class: '', rollNumber: '' }]
    }));
  };

  // Remove sibling
  const removeSibling = (index) => {
    if (formData.siblings.length > 1) {
      const newSiblings = formData.siblings.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        siblings: newSiblings
      }));
    }
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    const requiredFields = [
      'studentName', 'gender', 'dateOfBirth', 'placeOfBirth',
      'religion', 'nationality', 'email', 'fatherName',
      'address', 'phone', 'class', 'signature'
    ];

    requiredFields.forEach(field => {
      if (!formData[field].trim()) {
        newErrors[field] = 'This field is required';
      }
    });

    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation
    if (formData.phone && !/^\+?[\d\s-()]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (formData.cell && !/^\+?[\d\s-()]+$/.test(formData.cell)) {
      newErrors.cell = 'Please enter a valid cell number';
    }

    // Date validation
    if (formData.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      if (birthDate >= today) {
        newErrors.dateOfBirth = 'Birth date must be in the past';
      }
    }

    // Commented out admission date validation since field is removed
    // if (formData.admissionDate) {
    //   const admissionDate = new Date(formData.admissionDate);
    //   const today = new Date();
    //   if (admissionDate < today) {
    //     newErrors.admissionDate = 'Admission date must be in the future';
    //   }
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Set application date to today's date automatically
      const submissionData = {
        ...formData,
        applicationDate: getTodayDate()
      };

      // Submit to API endpoint
      const response = await fetch('/api/submit-application', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const result = await response.json();

      if (result.success) {
        // Success handling with dialog
        setSubmissionResult(result);
        setShowSuccessDialog(true);

        // Reset form
        setFormData({
          studentName: '',
          gender: '',
          dateOfBirth: '',
          placeOfBirth: '',
          religion: '',
          nationality: '',
          email: '',
          fatherName: '',
          address: '',
          phone: '',
          cell: '',
          class: '',
          admissionDate: '',
          lastSchool: '',
          reasonForLeaving: '',
          siblings: [{ name: '', class: '', rollNumber: '' }],
          applicationDate: '',
          signature: ''
        });
        setShowForm(false);
      } else {
        throw new Error(result.message || 'Failed to submit application');
      }

    } catch (error) {
      console.error('Submission error:', error);
      setErrorMessage(error.message || 'Failed to submit application. Please try again or contact us directly.');
      setShowErrorDialog(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  const admissionSteps = [
    {
      number: 1,
      title: "Submit Application",
      description: "Fill out the online application form or collect it from our front desk.",
      icon: DocumentTextIcon,
      color: "from-blue-500 to-blue-600"
    },
    {
      number: 2,
      title: "Meet & Assessment",
      description: "Our team will schedule an informal interaction with the child and parents. Early years and primary applicants might have a short interaction as an assessment.",
      icon: UserGroupIcon,
      color: "from-yellow-500 to-yellow-600"
    },
    {
      number: 3,
      title: "Admission Decision",
      description: "Parents will be informed of the admission status within 8-10 working days.",
      icon: ClipboardDocumentCheckIcon,
      color: "from-green-500 to-green-600"
    },
    {
      number: 4,
      title: "Fee Payment & Confirmation",
      description: "Upon acceptance, pay the admission fee to confirm your child's seat.",
      icon: CreditCardIcon,
      color: "from-purple-500 to-purple-600"
    }
  ];

  const ageCriteria = [
    { programme: "Early Year 1", ageGroup: "2.6-3.5 Years" },
    { programme: "Early Year 2", ageGroup: "3.6-4.5 Years" },
    { programme: "Kindergarten", ageGroup: "4.6-5.5 Years" },
    { programme: "Grade 1", ageGroup: "5.6-6.5 Years" },
    { programme: "Grade 2", ageGroup: "6.6-7.5 Years" },
    { programme: "Grade 3", ageGroup: "7.6-8.5 Years" },
  ];

  const programmes = [
    {
      title: "Early Years Programme",
      description: "Play-based learning with strong Islamic values for our youngest learners",
      icon: "🧸",
      features: ["Montessori approach", "Islamic tarbiyah", "Creative play", "Social skills development"]
    },
    {
      title: "Primary Programme",
      description: "Cambridge curriculum with integrated Hifz programme",
      icon: "📚",
      features: ["Cambridge Primary", "Qur'an memorization", "Arabic language", "STEM focus"]
    },
    {
      title: "School Activities",
      description: "Enriching activities to develop well-rounded personalities",
      icon: "🎨",
      features: ["Robotics club", "Art & crafts", "Archery", "Islamic studies"]
    }
  ];

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundImage: `url('${require('../../assets/background.jpeg')}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Hero Section */}
      <section className="relative flex items-center justify-center min-h-screen overflow-hidden">
        <div
          className="absolute inset-0 transform scale-110 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: `url('${require('../../assets/update/Apply Online - Hero Image.jpg')}')`
          }}

        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 max-w-4xl px-4 mx-auto text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <AcademicCapIcon className="w-16 h-16 mx-auto mb-6 text-yellow-400" />
            <h1 className="mb-6 text-5xl font-bold md:text-7xl" style={{ fontFamily: 'Playfair Display, serif' }}>
              Admissions Open  <span className="text-yellow-400">Join the Aureus Family</span>
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex justify-center"
            >
              <button
                onClick={() => {
                  // Download admission form
                  const link = document.createElement('a');
                  link.href = '/Admission Form.pdf';
                  link.download = 'Aureus Cambridge School - Admission Form.pdf';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-semibold text-black transition-colors duration-300 bg-yellow-400 rounded-full sm:w-auto hover:bg-yellow-500 group"
              >
                Download Admission Form
                <DocumentTextIcon className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Welcome Message Section */}
      <section className="py-20 bg-white/90 backdrop-blur-sm">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="max-w-4xl mx-auto text-xl leading-relaxed md:text-2xl text-slate-600">
              We welcome families who value academic excellence and a strong moral foundation. At
              Aureus, every child is nurtured to become a responsible, spiritually conscious
              and confident learner. Whether you're enrolling for Daycare, Early Years or Primary,
              we look forward to partnering with you in your child's journey.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Programmes Section */}
      <section className="py-20 bg-slate-50/90 backdrop-blur-sm">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 font-['Playfair_Display']">
              Our Programmes
            </h2>
            <p className="max-w-3xl mx-auto text-lg text-slate-600">
              Discover our comprehensive educational programmes designed to nurture young minds
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-3">
            {programmes.map((programme, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-8 transition-all duration-300 bg-white shadow-lg rounded-2xl hover:shadow-xl"
              >
                <div className="mb-4 text-4xl">{programme.icon}</div>
                <h3 className="mb-4 text-xl font-bold text-slate-900">{programme.title}</h3>
                <p className="mb-6 text-slate-600">{programme.description}</p>
                <ul className="space-y-2">
                  {programme.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-slate-700">
                      <CheckCircleIcon className="w-5 h-5 mr-2 text-yellow-400" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Admission Process Section */}
      <section className="py-20 bg-white/90 backdrop-blur-sm">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 font-['Playfair_Display']">
              Simple Admission Process
            </h2>
            <p className="max-w-3xl mx-auto text-lg text-slate-600">
              Our straightforward 4-step process makes joining the Aureus family easy
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {admissionSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="relative mb-6">
                  <div className={`w-20 h-20 mx-auto rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center shadow-lg`}>
                    <step.icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute flex items-center justify-center w-8 h-8 text-sm font-bold bg-yellow-400 rounded-full -top-2 -right-2 text-slate-900">
                    {step.number}
                  </div>
                </div>
                <h3 className="mb-4 text-xl font-bold text-slate-900">{step.title}</h3>
                <p className="leading-relaxed text-slate-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Age Criteria Section */}
      <section className="py-20 bg-slate-50/90 backdrop-blur-sm">
        <div className="max-w-5xl px-4 mx-auto sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-12 text-center"
          >
            <CalendarDaysIcon className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 font-['Playfair_Display']">
              Age Criteria
            </h2>
            <p className="text-lg text-slate-600">
              Find the right programme for your child's age group
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="overflow-hidden bg-white shadow-lg rounded-2xl"
          >
            <div className="px-8 py-6 bg-gradient-to-r from-yellow-400 to-yellow-500">
              <h3 className="text-xl font-bold text-slate-900">Programme Age Groups</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-8 py-4 font-semibold text-left text-slate-900">Programme</th>
                    <th className="px-8 py-4 font-semibold text-left text-slate-900">Age Group</th>
                  </tr>
                </thead>
                <tbody>
                  {ageCriteria.map((criteria, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="transition-colors border-b border-slate-100 hover:bg-slate-50"
                    >
                      <td className="px-8 py-6 font-medium text-slate-900">{criteria.programme}</td>
                      <td className="px-8 py-6 text-slate-600">{criteria.ageGroup}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Registration Form Section */}
      <section id="registration-section" className="py-20 bg-white/90 backdrop-blur-sm">
        <div className="max-w-4xl px-4 mx-auto sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-12 text-center"
          >
            <DocumentTextIcon className="w-16 h-16 mx-auto mb-6 text-yellow-400" />
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 font-['Playfair_Display']">
              Student Registration Form
            </h2>
            <p className="max-w-2xl mx-auto mb-8 text-lg text-slate-600">
              Complete the form below to apply for admission. All fields marked with * are required.
            </p>

            {!showForm ? (
              <div className="flex flex-col items-center justify-center max-w-md gap-4 mx-auto sm:flex-row">
                <button
                  onClick={() => setShowForm(true)}
                  className="flex items-center justify-center w-full px-8 py-4 font-semibold transition-all duration-300 bg-yellow-400 rounded-full hover:bg-yellow-500 text-slate-900 sm:w-auto"
                >
                  <DocumentTextIcon className="w-5 h-5 mr-2" />
                  Start Application
                </button>
                <a
                  href="tel:+923261116777"
                  className="flex items-center justify-center w-full px-8 py-4 font-semibold no-underline transition-all duration-300 border-2 rounded-full border-slate-900 hover:bg-slate-900 hover:text-white text-slate-900 sm:w-auto"
                >
                  <PhoneIcon className="w-5 h-5 mr-2" />
                  Call Us
                </a>
              </div>
            ) : (
              <motion.div
                id="application-form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="p-8 shadow-lg bg-slate-50 rounded-2xl"
              >
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Student Information Section */}
                  <div>
                    <h3 className="flex items-center mb-6 text-xl font-bold text-slate-900">
                      <UserIcon className="w-6 h-6 mr-2 text-yellow-400" />
                      Student Information
                    </h3>
                    <div className="grid gap-6 md:grid-cols-2">
                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          Student Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="studentName"
                          value={formData.studentName}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${errors.studentName ? 'border-red-500' : 'border-slate-300'
                            }`}
                          placeholder="Enter student's full name"
                        />
                        {errors.studentName && (
                          <p className="mt-1 text-sm text-red-500">{errors.studentName}</p>
                        )}
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          Gender <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="gender"
                          value={formData.gender}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${errors.gender ? 'border-red-500' : 'border-slate-300'
                            }`}
                        >
                          <option value="">Select gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                        {errors.gender && (
                          <p className="mt-1 text-sm text-red-500">{errors.gender}</p>
                        )}
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          Date of Birth <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="dateOfBirth"
                          value={formData.dateOfBirth}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${errors.dateOfBirth ? 'border-red-500' : 'border-slate-300'
                            }`}
                        />
                        {errors.dateOfBirth && (
                          <p className="mt-1 text-sm text-red-500">{errors.dateOfBirth}</p>
                        )}
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          Place of Birth <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="placeOfBirth"
                          value={formData.placeOfBirth}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${errors.placeOfBirth ? 'border-red-500' : 'border-slate-300'
                            }`}
                          placeholder="Enter place of birth"
                        />
                        {errors.placeOfBirth && (
                          <p className="mt-1 text-sm text-red-500">{errors.placeOfBirth}</p>
                        )}
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          Religion <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="religion"
                          value={formData.religion}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${errors.religion ? 'border-red-500' : 'border-slate-300'
                            }`}
                          placeholder="Enter religion"
                        />
                        {errors.religion && (
                          <p className="mt-1 text-sm text-red-500">{errors.religion}</p>
                        )}
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          Nationality <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="nationality"
                          value={formData.nationality}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${errors.nationality ? 'border-red-500' : 'border-slate-300'
                            }`}
                          placeholder="Enter nationality"
                        />
                        {errors.nationality && (
                          <p className="mt-1 text-sm text-red-500">{errors.nationality}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Contact Information Section */}
                  <div>
                    <h3 className="flex items-center mb-6 text-xl font-bold text-slate-900">
                      <EnvelopeIcon className="w-6 h-6 mr-2 text-yellow-400" />
                      Contact Information
                    </h3>
                    <div className="grid gap-6 md:grid-cols-2">
                      <div className="md:col-span-2">
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${errors.email ? 'border-red-500' : 'border-slate-300'
                            }`}
                          placeholder="Enter email address"
                        />
                        {errors.email && (
                          <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                        )}
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          Phone <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${errors.phone ? 'border-red-500' : 'border-slate-300'
                            }`}
                          placeholder="Enter phone number"
                        />
                        {errors.phone && (
                          <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                        )}
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          Cell Number
                        </label>
                        <input
                          type="tel"
                          name="cell"
                          value={formData.cell}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${errors.cell ? 'border-red-500' : 'border-slate-300'
                            }`}
                          placeholder="Enter cell number"
                        />
                        {errors.cell && (
                          <p className="mt-1 text-sm text-red-500">{errors.cell}</p>
                        )}
                      </div>

                      <div className="md:col-span-2">
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          Address <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          rows="3"
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${errors.address ? 'border-red-500' : 'border-slate-300'
                            }`}
                          placeholder="Enter complete address"
                        />
                        {errors.address && (
                          <p className="mt-1 text-sm text-red-500">{errors.address}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Parent Information Section */}
                  <div>
                    <h3 className="flex items-center mb-6 text-xl font-bold text-slate-900">
                      <UsersIcon className="w-6 h-6 mr-2 text-yellow-400" />
                      Parent Information
                    </h3>
                    <div className="grid gap-6 md:grid-cols-1">
                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          Father's Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="fatherName"
                          value={formData.fatherName}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${errors.fatherName ? 'border-red-500' : 'border-slate-300'
                            }`}
                          placeholder="Enter father's full name"
                        />
                        {errors.fatherName && (
                          <p className="mt-1 text-sm text-red-500">{errors.fatherName}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Academic Information Section */}
                  <div>
                    <h3 className="flex items-center mb-6 text-xl font-bold text-slate-900">
                      <AcademicCapIcon className="w-6 h-6 mr-2 text-yellow-400" />
                      Academic Information
                    </h3>
                    <div className="grid gap-6 md:grid-cols-2">
                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          Class/Grade <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="class"
                          value={formData.class}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${errors.class ? 'border-red-500' : 'border-slate-300'
                            }`}
                        >
                          <option value="">Select class</option>
                          <option value="Early Year 1">Early Year 1</option>
                          <option value="Early Year 2">Early Year 2</option>
                          <option value="Kindergarten">Kindergarten</option>
                          <option value="Grade 1">Grade 1</option>
                          <option value="Grade 2">Grade 2</option>
                        </select>
                        {errors.class && (
                          <p className="mt-1 text-sm text-red-500">{errors.class}</p>
                        )}
                      </div>

                      {/* Commented out Admission Date field
                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          Admission Date *
                        </label>
                        <input
                          type="date"
                          name="admissionDate"
                          value={formData.admissionDate}
                          onChange={handleInputChange}
                          min={getTodayDate()}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${errors.admissionDate ? 'border-red-500' : 'border-slate-300'
                            }`}
                        />
                        {errors.admissionDate && (
                          <p className="mt-1 text-sm text-red-500">{errors.admissionDate}</p>
                        )}
                      </div>
                      */}

                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          Last School
                        </label>
                        <input
                          type="text"
                          name="lastSchool"
                          value={formData.lastSchool}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border rounded-lg border-slate-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                          placeholder="Enter last school name"
                        />
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          Reason for Leaving
                        </label>
                        <input
                          type="text"
                          name="reasonForLeaving"
                          value={formData.reasonForLeaving}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border rounded-lg border-slate-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                          placeholder="Enter reason for leaving"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Siblings Information Section */}
                  <div>
                    <h3 className="flex items-center mb-6 text-xl font-bold text-slate-900">
                      <UserGroupIcon className="w-6 h-6 mr-2 text-yellow-400" />
                      Other Children studying in Aureus
                    </h3>
                    {formData.siblings.map((sibling, index) => (
                      <div key={index} className="p-4 mb-4 border rounded-lg border-slate-200">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-medium text-slate-700">Child {index + 1}</h4>
                          {formData.siblings.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeSibling(index)}
                              className="p-1 text-red-500 hover:text-red-700"
                            >
                              <MinusIcon className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                        <div className="grid gap-4 md:grid-cols-3">
                          <div>
                            <label className="block mb-2 text-sm font-medium text-slate-700">
                              Name
                            </label>
                            <input
                              type="text"
                              value={sibling.name}
                              onChange={(e) => handleSiblingChange(index, 'name', e.target.value)}
                              className="w-full px-4 py-3 border rounded-lg border-slate-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                              placeholder="Enter sibling's name"
                            />
                          </div>
                          <div>
                            <label className="block mb-2 text-sm font-medium text-slate-700">
                              Class
                            </label>
                            <input
                              type="text"
                              value={sibling.class}
                              onChange={(e) => handleSiblingChange(index, 'class', e.target.value)}
                              className="w-full px-4 py-3 border rounded-lg border-slate-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                              placeholder="Enter class"
                            />
                          </div>
                          <div>
                            <label className="block mb-2 text-sm font-medium text-slate-700">
                              Roll Number
                            </label>
                            <input
                              type="text"
                              value={sibling.rollNumber}
                              onChange={(e) => handleSiblingChange(index, 'rollNumber', e.target.value)}
                              className="w-full px-4 py-3 border rounded-lg border-slate-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                              placeholder="Enter roll number"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addSibling}
                      className="flex items-center font-medium text-blue-600 hover:text-blue-700"
                    >
                      <PlusIcon className="w-5 h-5 mr-1" />
                      Add Another Child
                    </button>
                  </div>

                  {/* Application Date and Signature Section */}
                  <div>
                    <h3 className="flex items-center mb-6 text-xl font-bold text-slate-900">
                      <IdentificationIcon className="w-6 h-6 mr-2 text-yellow-400" />
                      Application Details
                    </h3>
                    <div className="grid gap-6 md:grid-cols-2">
                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          Application Date
                        </label>
                        <div className="w-full px-4 py-3 border rounded-lg bg-gray-50 border-slate-300 text-slate-600">
                          {getTodayDate()}
                        </div>
                      </div>
                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          Signature (Full Name) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="signature"
                          value={formData.signature}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${errors.signature ? 'border-red-500' : 'border-slate-300'
                            }`}
                          placeholder="Enter your full name as signature"
                        />
                        {errors.signature && (
                          <p className="mt-1 text-sm text-red-500">{errors.signature}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex flex-col justify-center gap-4 pt-6 sm:flex-row">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex items-center justify-center w-full px-8 py-4 font-semibold transition-all duration-300 bg-yellow-400 rounded-full hover:bg-yellow-500 disabled:bg-yellow-300 text-slate-900 sm:w-auto"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 mr-2 border-b-2 rounded-full animate-spin border-slate-900"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <DocumentTextIcon className="w-5 h-5 mr-2" />
                          Submit Application
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="flex items-center justify-center w-full px-8 py-4 font-semibold transition-all duration-300 border-2 rounded-full border-slate-300 hover:border-slate-400 text-slate-700 sm:w-auto"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </motion.div>
            )}
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="p-8 mt-16 text-center bg-yellow-400 rounded-2xl"
          >
            <UsersIcon className="w-12 h-12 mx-auto mb-4 text-slate-900" />
            <h3 className="mb-4 text-2xl font-bold text-slate-900">
              Need Help with Your Application?
            </h3>
            <p className="max-w-2xl mx-auto mb-6 text-slate-800">
              Our admissions team is here to assist you throughout the process.
              Feel free to contact us if you have any questions.
            </p>
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a
                href="tel:+923261116777"
                className="flex items-center justify-center px-6 py-3 font-semibold text-white no-underline transition-all duration-300 rounded-full bg-slate-900 hover:bg-slate-800"
              >
                <PhoneIcon className="w-5 h-5 mr-2" />
                +92 326 1116777
              </a>
              <div className="text-slate-800">
                <p className="font-medium">📍 F-78, Block 'B', North Nazimabad, Karachi</p>
                <p className="text-sm">Office Hours: Monday - Friday, 8:00 AM - 4:00 PM</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Success Dialog */}
      {showSuccessDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 500 }}
            className="w-full max-w-lg p-8 bg-white border border-gray-100 shadow-2xl rounded-3xl"
          >
            <div className="text-center">
              {/* Animated Success Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", damping: 10, stiffness: 200 }}
                className="flex items-center justify-center w-20 h-20 mx-auto mb-6 bg-green-500 rounded-full shadow-lg"
              >
                <CheckCircleIcon className="w-10 h-10 text-white" />
              </motion.div>

              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mb-3 text-2xl font-bold text-slate-900"
              >
                🎉 Application Submitted Successfully!
              </motion.h3>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mb-6 leading-relaxed text-slate-600"
              >
                Thank you for applying to <strong>Aureus Cambridge School</strong>. Your application has been received and is now being processed by our admissions team.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="p-6 mb-6 border-l-4 border-yellow-400 rounded-2xl bg-yellow-50"
              >
                <p className="mb-2 text-sm font-medium text-yellow-800">
                  📋 Your Submission ID
                </p>
                <p className="font-mono text-2xl font-bold tracking-wider text-yellow-900">
                  {submissionResult?.submissionId}
                </p>
                <p className="mt-2 text-xs text-yellow-700">
                  Please save this ID for future reference
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="mb-8 space-y-2 text-sm text-slate-600"
              >
                <div className="flex items-center justify-center gap-2">
                  <span className="text-lg">📧</span>
                  <p>A confirmation email has been sent to your registered email address</p>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-lg">📞</span>
                  <p>We will contact you within <strong>8-10 working days</strong> to schedule an assessment</p>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-lg">🎓</span>
                  <p>Welcome to the Aureus Cambridge School family!</p>
                </div>
              </motion.div>

              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setShowSuccessDialog(false);
                  navigate('/');
                }}
                className="w-full px-8 py-4 font-semibold text-black transition-all duration-300 bg-yellow-400 rounded-2xl hover:bg-yellow-500 focus:outline-none focus:ring-4 focus:ring-yellow-400 focus:ring-offset-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Continue to Homepage
              </motion.button>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="mt-4 text-xs text-slate-400"
              >
                Thank you for choosing Aureus Cambridge School
              </motion.p>
            </div>
          </motion.div>
        </div>
      )}

      {/* Error Dialog */}
      {showErrorDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 500 }}
            className="w-full max-w-lg p-8 bg-white border border-gray-100 shadow-2xl rounded-3xl"
          >
            <div className="text-center">
              {/* Animated Error Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", damping: 10, stiffness: 200 }}
                className="flex items-center justify-center w-20 h-20 mx-auto mb-6 rounded-full shadow-lg bg-gradient-to-r from-red-400 to-red-600"
              >
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </motion.div>

              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mb-3 text-2xl font-bold text-slate-900"
              >
                ⚠️ Application Submission Failed
              </motion.h3>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mb-6 leading-relaxed text-slate-600"
              >
                {errorMessage}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="p-6 mb-6 border-l-4 border-red-400 rounded-2xl bg-gradient-to-r from-red-50 to-pink-50"
              >
                <p className="mb-2 text-sm text-red-800">
                  Please check your internet connection and try again. If the problem persists, contact us directly.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="mb-8 space-y-2 text-sm text-slate-600"
              >
                <div className="flex items-center justify-center gap-2">
                  <span className="text-lg">📞</span>
                  <p>Contact us at: <strong>+92 326 1116777</strong></p>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-lg">📧</span>
                  <p>Email: <strong>admissions@aureusschool.com</strong></p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="flex gap-4"
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowErrorDialog(false)}
                  className="flex-1 px-6 py-3 font-semibold transition-all duration-300 text-slate-700 bg-slate-200 rounded-xl hover:bg-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
                >
                  Close
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setShowErrorDialog(false);
                    // Scroll to top of form for retry
                    document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="flex-1 px-6 py-3 font-semibold text-white transition-all duration-300 shadow-lg rounded-xl bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 hover:shadow-xl"
                >
                  Try Again
                </motion.button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ApplyOnline; 